import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import {
  approveTransfer,
  getTransfer,
  receiveTransfer,
  rejectTransfer,
  shipTransfer,
} from '@/lib/api/inventory-transfers.js';
import { useAuthStore } from '@/stores/auth.js';
import { formatDateTime, prettyLabel } from '@/lib/format.js';
import { parseApiError } from '@/lib/api/errors.js';
import { toastError, toastSuccess } from '@/lib/toast.js';

export function TransferDetailPage() {
  const { id = '' } = useParams<{ id: string }>();
  const storeId = useAuthStore((s) => s.user?.storeId);
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['inventory-transfer', id], queryFn: () => getTransfer(id), enabled: Boolean(id) });

  const approveM = useMutation({
    mutationFn: () => approveTransfer(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['inventory-transfer', id] });
      toastSuccess('Transfer approved');
    },
    onError: (e) => toastError(parseApiError(e).message),
  });
  const rejectM = useMutation({
    mutationFn: () => rejectTransfer(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['inventory-transfer', id] });
      toastSuccess('Transfer rejected');
    },
    onError: (e) => toastError(parseApiError(e).message),
  });
  const shipM = useMutation({
    mutationFn: () => shipTransfer(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['inventory-transfer', id] });
      toastSuccess('Marked as shipped');
    },
    onError: (e) => toastError(parseApiError(e).message),
  });
  const recvM = useMutation({
    mutationFn: () => receiveTransfer(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['inventory-transfer', id] });
      toastSuccess('Receipt confirmed');
    },
    onError: (e) => toastError(parseApiError(e).message),
  });

  if (q.isLoading || !q.data) return <div className="text-slate-600">Loading…</div>;
  const t = q.data;
  const isFrom = t.fromStoreId === storeId;
  const isTo = t.toStoreId === storeId;

  return (
    <div className="space-y-6">
      <Link to="/inventory/transfers" className="text-sm font-semibold text-primary">
        ← Transfers
      </Link>
      <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <h1 className="text-xl font-bold text-slate-950">Transfer {t.id.slice(0, 8)}…</h1>
        <p className="text-sm text-slate-600">
          {t.fromStoreName} → {t.toStoreName}
        </p>
        <p className="mt-2 text-sm font-semibold text-slate-800">{prettyLabel(t.status)}</p>
        <p className="text-xs text-slate-500">Created {formatDateTime(t.createdAt)}</p>

        <div className="mt-4 flex flex-wrap gap-2">
          {t.status === 'requested' && isFrom ? (
            <>
              <button
                type="button"
                className="rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white"
                onClick={() => approveM.mutate()}
                disabled={approveM.isPending}
              >
                Approve
              </button>
              <button
                type="button"
                className="rounded-xl border border-red-200 px-4 py-2 text-sm font-semibold text-red-800"
                onClick={() => rejectM.mutate()}
                disabled={rejectM.isPending}
              >
                Reject
              </button>
            </>
          ) : null}
          {t.status === 'approved' && isFrom ? (
            <button
              type="button"
              className="rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white"
              onClick={() => shipM.mutate()}
              disabled={shipM.isPending}
            >
              Mark as shipped
            </button>
          ) : null}
          {t.status === 'shipped' && isTo ? (
            <button
              type="button"
              className="rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white"
              onClick={() => recvM.mutate()}
              disabled={recvM.isPending}
            >
              Confirm receipt
            </button>
          ) : null}
        </div>

        <div className="mt-6 overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
              <tr>
                <th className="px-4 py-2">Item</th>
                <th className="px-4 py-2">Qty</th>
                <th className="px-4 py-2">Serial</th>
              </tr>
            </thead>
            <tbody>
              {t.lines.map((l) => (
                <tr key={l.id} className="border-t border-slate-100">
                  <td className="px-4 py-2 font-medium">{l.itemName}</td>
                  <td className="px-4 py-2">{l.qtyRequested}</td>
                  <td className="px-4 py-2 font-mono text-xs">{l.serialId ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {t.notes ? <p className="mt-4 text-sm text-slate-600">{t.notes}</p> : null}
      </div>
    </div>
  );
}
